import React from 'react';
import type { WizardState } from '../types';
import type { StepError } from '../validateStep';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors?: StepError[];
}

const VOLUME_BANDS = [
  { code: 'xs', label: 'XS', max: 100, desc: 'Up to 100 series/year' },
  { code: 'small', label: 'Small', max: 500, desc: 'Up to 500 series/year' },
  { code: 'medium', label: 'Medium', max: 2000, desc: 'Up to 2,000 series/year' },
  { code: 'large', label: 'Large', max: 10000, desc: 'Up to 10,000 series/year' },
  { code: 'xl', label: 'XL', max: 50000, desc: 'Up to 50,000 series/year' },
  { code: 'enterprise', label: 'Enterprise', max: 999999, desc: 'Unlimited' },
  { code: 'custom', label: 'Custom', max: 0, desc: 'Custom volume' },
];

export default function InspectionPlanStep({ state, onChange, errors = [] }: Props) {
  const p = state.inspectionPlan;
  const update = (patch: Record<string, any>) => onChange({ inspectionPlan: { ...p, ...patch } });

  const handleBandChange = (code: string) => {
    const band = VOLUME_BANDS.find((b) => b.code === code);
    update({ volumeBand: code, maxSeriesPerYear: band?.max || p.maxSeriesPerYear });
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Volume Band</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {VOLUME_BANDS.map((band) => (
            <button
              key={band.code}
              type="button"
              onClick={() => handleBandChange(band.code)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                p.volumeBand === band.code
                  ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-semibold text-gray-900">{band.label}</div>
              <p className="text-xs text-gray-500 mt-0.5">{band.desc}</p>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Plan Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Series Per Year</label>
            <input
              type="number"
              min="1"
              value={p.maxSeriesPerYear ?? 500}
              onChange={(e) => update({ maxSeriesPerYear: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commitment Term</label>
            <select value={p.commitmentTerm || 'annual'} onChange={(e) => update({ commitmentTerm: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
              <option value="3year">3-Year</option>
              <option value="5year">5-Year</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Annual Price (USD)</label>
            <input type="number" min="0" step="0.01" value={p.annualPrice ?? 0} onChange={(e) => update({ annualPrice: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Overage Policy</label>
            <select value={p.overagePolicy || 'block'} onChange={(e) => update({ overagePolicy: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="block">Block (hard limit)</option>
              <option value="per_series">Per-Series Overage</option>
              <option value="auto_upgrade">Auto-Upgrade Band</option>
            </select>
          </div>
          {p.overagePolicy === 'per_series' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overage Rate ($/series)</label>
              <input type="number" min="0" step="0.01" value={p.overageRate ?? 0} onChange={(e) => update({ overageRate: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Source</label>
            <select value={p.source || 'department'} onChange={(e) => update({ source: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value="department">Department</option>
              <option value="organization">Organization</option>
            </select>
          </div>
        </div>
      </section>
    </div>
  );
}
